// components/VisTimeline.tsx
import { Autocomplete, Box, Card, CardContent, Stack, TextField, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { DataSet, Timeline, TimelineOptions } from 'vis-timeline/standalone';
import BreastCancerEMRCard from './BreastCancerEMRCard';
import BreastCancerLISCard from './BreastCancerLISCard';
import './timelineCustom.css';
import Grid from "@mui/material/Grid2";
import AuditDetailsCard from '../home/AuditDetailsCard';
import BreastCancerIMSCard from './BreastCancerIMSCard';
import AIBreastCancerCard from './AIBreastCancerCard';

interface TimelineItem {
  id: number;
  content: string;
  start: string;
  end?: string;
}

interface TimelineProps {
  items: TimelineItem[];
  onSelectItem?: (itemId: number) => void;
  documentData?: any;
  selectedFilterTypeValue?: number;
  onFilterChange?: (filterValue: number) => void;
}

const TimelineComponent: React.FC<TimelineProps> = ({ items, onSelectItem, selectedFilterTypeValue, documentData, onFilterChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);
  const theme = useTheme();
  const filterTypes = [
    { value: 1, displayValue: 'EMR' },
    { value: 2, displayValue: 'LIS' },
    { value: 3, displayValue: 'IMS' },
    { value: 4, displayValue: 'AI' },
  ];
  const [selectedFilterType, setSelectedFilterType] = React.useState(filterTypes[0]);


  useEffect(() => {
    if (!containerRef.current) return;
    const dataSet = new DataSet(items);

    const options: TimelineOptions = {
      stack: true,
      editable: false,
      margin: {
        item: 10,
        axis: 5,
      },
      orientation: 'bottom',
    };

    const timeline = new Timeline(containerRef.current, dataSet, options);
    timelineRef.current = timeline;

    // Handle item click/select
    if (onSelectItem) {
      timeline.on('select', (props) => {
        const [selectedId] = props.items;
        if (selectedId) {
          onSelectItem(Number(selectedId));
        }
      });
    }

    return () => {
      timeline.destroy();
    };
  }, [items, onSelectItem]);

  // Determine the CSS class based on theme mode
  const timelineClassName = theme.palette.mode === 'dark' ? 'timeline-dark' : 'timeline-light';

  useEffect(() => {
    if (selectedFilterTypeValue) {
      const matched = filterTypes.find(f => f.value === selectedFilterTypeValue);
      if (matched) {
        setSelectedFilterType(matched);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilterTypeValue]);

  const handleFilterChange = (newValue: any) => {
    setSelectedFilterType(newValue);
    // Notify parent component of the change
    if (onFilterChange) {
      onFilterChange(newValue.value);
    }
  };

  return (
    <Stack
      sx={{
        flexDirection: "column",
        justifyContent: "center",
        backgroundColor: "background.paper",
      }}
    >
      {documentData && (
        <AuditDetailsCard documentData={documentData} />
      )}
      <Box sx={{ p: 3 }}>
        <Card variant='outlined' sx={{ borderRadius: 2 }}>
          <CardContent>
            <Grid
              container
              direction="column"
              sx={{
                justifyContent: "flex-start",
                alignItems: "stretch",
              }}
              spacing={2}
            >
              <Grid>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Timeline View
                </Typography>
              </Grid>
              <Grid>
                <Grid>
                  <Autocomplete
                    value={selectedFilterType}
                    disablePortal
                    id="controllable-states-demo"
                    getOptionLabel={(option) => `${option.displayValue}`}
                    options={filterTypes}
                    disableClearable
                    // onChange={(_event: any, newValue: any) => {
                    //   setSelectedFilterType(newValue);
                    // }}
                    onChange={(_event: any, newValue: any) => {
                      handleFilterChange(newValue); // Use the new handler
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Filter Type" />
                    )}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
              <Grid>
                <div
                  ref={containerRef}
                  className={timelineClassName}
                  style={{
                    height: '200px',
                    width: '100%',
                    color: theme.palette.text.primary
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
      {selectedFilterTypeValue === 1 ? <BreastCancerEMRCard /> : null}
      {selectedFilterTypeValue === 2 ? <BreastCancerLISCard /> : null}
      {selectedFilterTypeValue === 3 ? <BreastCancerIMSCard /> : null}
      {selectedFilterTypeValue === 4 ? <Box sx={{ width: '100%', overflow: 'auto' }}>
        <AIBreastCancerCard />
      </Box> : null}
    </Stack>
  );
};

export default TimelineComponent;